"use client";

import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryClient } from "@/lib/query-client";
import { fetchCityEvents } from "@/app/[locale]/[city]/actions";
import { getCitySlugForVenueCity, isPublicCitySlug, PUBLIC_CITIES } from "@/lib/constants";
import { isEventInFeed } from "@finda/shared/event-expiry";
import { relatedJukeboxStart } from "@/lib/jukebox-playlist";
import { useSpotifyPlayer, type JukeboxLaunchTarget } from "./spotify-player-context";
import { useJukeboxLauncher } from "./use-jukebox-launcher";
import { useJukeboxReopen } from "./use-jukebox-reopen";

/** Artist and event pages open a genre-ordered queue without leaving the page. */
export function useRelatedJukebox(defaultCitySlug: string, blockedSpotifyIds: string[]) {
  const { playbackTarget, relatedSession, setRelatedSession } = useSpotifyPlayer();
  const [requested, setRequested] = useState(false);
  const [target, setTarget] = useState<JukeboxLaunchTarget | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [resumeIndex, setResumeIndex] = useState<number | null>(null);
  const [version, setVersion] = useState(0);
  const sourceEvent = isOpen ? target?.event : undefined;
  const citySlug = sourceEvent?.venue.city_slug ?? getCitySlugForVenueCity(sourceEvent?.venue.city ?? "") ?? defaultCitySlug;
  const query = useQuery({
    queryKey: ["related-jukebox", citySlug],
    queryFn: () => fetchCityEvents(citySlug),
    enabled: (requested || !!playbackTarget) && isPublicCitySlug(citySlug),
    staleTime: 5 * 60 * 1000,
  }, getQueryClient());
  const events = useMemo(() => isPublicCitySlug(citySlug)
    ? (query.data ?? []).filter((event) => isEventInFeed(event, PUBLIC_CITIES[citySlug].timezone)) : [], [query.data, citySlug]);
  const getStart = useCallback((target: JukeboxLaunchTarget) => {
    if (relatedSession?.citySlug === citySlug && relatedSession.target.event.id === target.event.id
      && relatedSession.target.artistId === target.artistId) return relatedSession.start;
    return relatedJukeboxStart(events, target.event, target.artistId, blockedSpotifyIds);
  }, [relatedSession, citySlug, events, blockedSpotifyIds]);
  const openJukebox = useCallback((target: JukeboxLaunchTarget) => {
    setRequested(true);
    setTarget(target);
    setResumeIndex(null);
    setVersion((previous) => previous + 1);
    setIsOpen(true);
  }, []);
  useJukeboxLauncher(openJukebox);
  useJukeboxReopen(isOpen, () => setIsOpen(true));
  const start = useMemo(() => target ? getStart(target) : null, [getStart, target]);
  const onClose = useCallback((index: number) => {
    setResumeIndex(index);
    setIsOpen(false);
    if (start && playbackTarget) {
      setTarget(playbackTarget);
      // A closed, still-loading playlist must be able to fill in when its read finishes.
      if (query.data !== undefined) {
        setRelatedSession({ citySlug, start: { ...start, index, artistId: playbackTarget.artistId }, target: playbackTarget });
      }
    }
  }, [start, playbackTarget, citySlug, setRelatedSession, query.data]);
  return {
    openJukebox,
    modalKey: version,
    modalProps: isOpen && start?.playlist.length ? {
      playlist: start.playlist, citySlug,
      initialIndex: resumeIndex ?? start.index,
      initialArtistId: resumeIndex !== null ? playbackTarget?.artistId : start.artistId,
      playlistPending: query.isPending,
      playlistError: query.isError,
      onRetryPlaylist: () => { void query.refetch(); },
      onClose,
    } : null,
  };
}
